import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Database, RefreshCw, Play, Pause, ArrowLeft, CheckCircle2,
  XCircle, AlertTriangle, ShieldAlert, Radio, ScrollText, RotateCw,
  ToggleLeft, ToggleRight, Power, Layers,
} from 'lucide-react';

const STAT_TONE = {
  PASS: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  WARNING: 'text-amber-700 bg-amber-50 border-amber-200',
  FAIL: 'text-rose-700 bg-rose-50 border-rose-200',
};

const Card = ({ title, value, tone = 'indigo', icon: Icon, sub, testid }) => (
  <div data-testid={testid} className={`rounded-xl border bg-white p-4 border-${tone}-100`}>
    <div className="flex items-center gap-2 text-xs text-gray-500">
      {Icon && <Icon className={`w-3.5 h-3.5 text-${tone}-700`} />}
      <span>{title}</span>
    </div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const StatusRow = ({ label, ok, sub, testid, icon: Icon }) => (
  <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${
    ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'
  }`} data-testid={testid}>
    <div className="flex items-center gap-2.5">
      {Icon && <Icon className={`w-4 h-4 ${ok ? 'text-emerald-700' : 'text-amber-700'}`} />}
      <div>
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
      </div>
    </div>
    {ok
      ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
      : <AlertTriangle className="w-5 h-5 text-amber-600" />}
  </div>
);

const Btn = ({ onClick, children, variant = 'primary', disabled, testid }) => {
  const styles = {
    primary: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    danger: 'bg-rose-600 hover:bg-rose-700 text-white',
    ghost: 'border border-gray-200 text-gray-700 hover:bg-gray-50',
    warn: 'bg-amber-500 hover:bg-amber-600 text-white',
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${styles}`}>
      {children}
    </button>
  );
};

const MigrationCenter = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dry, setDry] = useState(null);
  const [verify, setVerify] = useState(null);
  const [sweep, setSweep] = useState(null);
  const [audit, setAudit] = useState([]);
  const [flags, setFlags] = useState(null);
  const [tenantAudit, setTenantAudit] = useState(null);
  const [leaks, setLeaks] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    setBusy('refresh');
    try {
      const [st, dr, au, fl, ta, lk] = await Promise.all([
        axiosInstance.get('/migration/status'),
        axiosInstance.get('/migration/backfill/dry-run'),
        axiosInstance.get('/migration/canary/audit', { params: { limit: 20 } }),
        axiosInstance.get('/router-flags/status'),
        axiosInstance.get('/tenant-audit/stats'),
        axiosInstance.get('/tenant-audit/leaks', { params: { limit: 20 } }),
      ]);
      setStatus(st.data);
      setDry(dr.data);
      setAudit(au.data.items || []);
      setFlags(fl.data);
      setTenantAudit(ta.data);
      setLeaks(lk.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy('refresh');
      try {
        const [st, dr, au, fl, ta, lk] = await Promise.all([
          axiosInstance.get('/migration/status'),
          axiosInstance.get('/migration/backfill/dry-run'),
          axiosInstance.get('/migration/canary/audit', { params: { limit: 20 } }),
          axiosInstance.get('/router-flags/status'),
          axiosInstance.get('/tenant-audit/stats'),
          axiosInstance.get('/tenant-audit/leaks', { params: { limit: 20 } }),
        ]);
        if (cancelled) return;
        setStatus(st.data); setDry(dr.data); setAudit(au.data.items || []);
        setFlags(fl.data); setTenantAudit(ta.data); setLeaks(lk.data.items || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Failed to load');
      } finally {
        if (!cancelled) setBusy(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setAuditMode = async (mode) => {
    if (mode === 'block' && !window.confirm(
      'BLOCK mode replaces any leaky response with a 500 error. Only use in dev/staging. Continue?'
    )) return;
    setBusy(`audit-${mode}`);
    try {
      const { data } = await axiosInstance.post('/tenant-audit/mode', { mode });
      setTenantAudit(data);
      toast.success(`Audit mode → ${mode}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const resetAudit = async () => {
    setBusy('audit-reset');
    try {
      const { data } = await axiosInstance.post('/tenant-audit/reset');
      setTenantAudit(data);
      toast.success('Stats reset');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const runApply = async () => {
    const txt = window.prompt('Type BACKFILL-CONFIRM to apply the backfill.', '');
    if (txt !== 'BACKFILL-CONFIRM') {
      if (txt !== null) toast.error('Confirmation text did not match.');
      return;
    }
    setBusy('apply');
    try {
      const { data } = await axiosInstance.post('/migration/backfill/apply', {
        target_company: 'cmp_001', confirm_token: 'BACKFILL-CONFIRM',
      });
      toast.success(`Backfill complete — ${data.total_updated} doc(s) updated`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    } finally { setBusy(null); }
  };

  const runVerify = async () => {
    setBusy('verify');
    try {
      const { data } = await axiosInstance.get('/migration/verify');
      setVerify(data);
      toast[data.grade === 'PASS' ? 'success' : 'warning'](
        `Verify: ${data.grade} — missing=${data.total_missing} mismatches=${data.mismatched_collections}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verify failed');
    } finally { setBusy(null); }
  };

  const runRebuild = async () => {
    setBusy('rebuild');
    try {
      const { data } = await axiosInstance.post('/migration/indexes/rebuild');
      const created = (data.rows || []).filter((r) => r.status === 'created').length;
      toast.success(`Indexes rebuild done — ${created} created`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rebuild failed');
    } finally { setBusy(null); }
  };

  const toggleCanary = async (action) => {
    setBusy(`canary-${action}`);
    try {
      await axiosInstance.post(`/migration/canary/${action}`);
      toast.success(`Canary ${action}ped`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const runSweep = async () => {
    setBusy('sweep');
    try {
      const { data } = await axiosInstance.post('/migration/canary/sweep', {
        target_company: 'cmp_001',
      });
      setSweep(data);
      toast[data.mismatches === 0 ? 'success' : 'error'](
        `Sweep: ${data.matches}/${data.checked} match · ${data.mismatches} mismatch`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sweep failed');
    } finally { setBusy(null); }
  };

  const toggleRouter = async (routerId, currentStatus) => {
    const next = currentStatus === 'STRICT' ? 'LEGACY' : 'STRICT';
    setBusy(`router-${routerId}`);
    try {
      const { data } = await axiosInstance.post('/router-flags/set',
        { router: routerId, status: next });
      setFlags(data);
      toast.success(`${routerId} → ${next}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const flipKillSwitch = async () => {
    const willEnable = flags?.kill_switch_active;
    const warn = willEnable
      ? 'Re-enable multi-company enforcement?'
      : 'KILL SWITCH: revert ALL routers to LEGACY immediately. Continue?';
    if (!window.confirm(warn)) return;
    setBusy('kill-switch');
    try {
      const { data } = await axiosInstance.post('/router-flags/kill-switch',
        { enabled: willEnable });
      setFlags(data);
      toast[willEnable ? 'success' : 'warning'](
        willEnable ? 'Enforcement re-enabled' : 'Kill switch active — all routers LEGACY'
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const backfillOk = status?.backfill?.done;
  const indexOk = (status?.indexes?.missing_collections ?? 1) === 0;
  const canaryEnabled = !!status?.canary?.enabled;
  const mismatchCount = status?.canary?.mismatches ?? 0;
  const verifyOk = verify?.grade === 'PASS';
  const overallGrade = verify?.grade
    || (backfillOk && indexOk && canaryEnabled && mismatchCount === 0 ? 'PASS' : 'WARNING');

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="migration-center-page">
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-2 text-sm text-indigo-700 hover:underline"
        data-testid="migration-back"
      >
        <ArrowLeft className="w-4 h-4" /> Settings
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-indigo-700" />
            Migration Center
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            P14-B — Dual-Read Canary · Backfill · Verification · Index Health
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="ghost" onClick={refresh} testid="migration-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${busy === 'refresh' ? 'animate-spin' : ''}`} /> Refresh
          </Btn>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card icon={Database} title="Phase"
              value={status?.phase || '—'}
              sub={backfillOk ? 'Backfill applied' : 'Pre-backfill (A)'}
              testid="migration-stat-phase" />
        <Card icon={CheckCircle2} title="Missing company_id"
              value={status?.backfill?.missing_total ?? '—'}
              sub={`${dry?.rows?.length || 0} tenant collections inspected`}
              testid="migration-stat-missing" />
        <Card icon={Radio} title="Canary"
              value={canaryEnabled ? 'ON' : 'OFF'}
              sub={`${status?.canary?.checks ?? 0} checks · ${mismatchCount} mismatch`}
              testid="migration-stat-canary" />
        <Card icon={ScrollText} title="Audit entries"
              value={status?.audit?.total_entries ?? 0}
              testid="migration-stat-audit" />
      </div>

      {/* Check matrix */}
      <div className={`rounded-xl border bg-white p-4 ${STAT_TONE[overallGrade] || ''}`}
           data-testid="migration-matrix">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Migration Checklist</h3>
          <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded border bg-white">
            {overallGrade}
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <StatusRow label="Backfill applied" ok={backfillOk}
                     sub={`${status?.backfill?.missing_total ?? '—'} doc(s) still missing company_id`}
                     icon={Database} testid="check-backfill" />
          <StatusRow label="Verification grade" ok={verifyOk}
                     sub={verify ? `Missing=${verify.total_missing} · Mismatch=${verify.mismatched_collections}` : 'Run verify to populate'}
                     icon={CheckCircle2} testid="check-verify" />
          <StatusRow label="Index health" ok={indexOk}
                     sub={`${status?.indexes?.missing_collections ?? '—'}/${status?.indexes?.total_collections ?? '—'} collections without company_id index`}
                     icon={RotateCw} testid="check-indexes" />
          <StatusRow label="Canary active" ok={canaryEnabled}
                     sub={canaryEnabled ? 'Dual-read shadow comparison live' : 'Off — enable for 24-72h window'}
                     icon={Radio} testid="check-canary" />
          <StatusRow label="Mismatch count = 0" ok={mismatchCount === 0}
                     sub={`Lifetime mismatches: ${mismatchCount}`}
                     icon={XCircle} testid="check-mismatches" />
        </div>
      </div>

      {/* Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border rounded-xl p-4" data-testid="action-backfill">
          <h4 className="font-bold text-sm mb-1">B1 — Backfill</h4>
          <p className="text-[11px] text-gray-500 mb-2">Stamp <code>company_id=cmp_001</code> on every legacy doc.</p>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={refresh} testid="backfill-dry-run">Dry-run</Btn>
            <Btn variant="warn" onClick={runApply} testid="backfill-apply" disabled={!!busy}>Apply</Btn>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4" data-testid="action-verify">
          <h4 className="font-bold text-sm mb-1">B2 — Verify</h4>
          <p className="text-[11px] text-gray-500 mb-2">Counts + checksums + index health.</p>
          <Btn onClick={runVerify} testid="run-verify" disabled={!!busy}>
            <Play className="w-3 h-3" /> Verify
          </Btn>
        </div>
        <div className="bg-white border rounded-xl p-4" data-testid="action-indexes">
          <h4 className="font-bold text-sm mb-1">B2 — Indexes</h4>
          <p className="text-[11px] text-gray-500 mb-2">Add `company_id` index where missing.</p>
          <Btn onClick={runRebuild} testid="run-rebuild" disabled={!!busy}>
            <RotateCw className="w-3 h-3" /> Rebuild
          </Btn>
        </div>
        <div className="bg-white border rounded-xl p-4" data-testid="action-canary">
          <h4 className="font-bold text-sm mb-1">B0/B3 — Canary</h4>
          <p className="text-[11px] text-gray-500 mb-2">Dual-read shadow comparator.</p>
          <div className="flex gap-1.5 flex-wrap">
            {canaryEnabled
              ? <Btn variant="danger" onClick={() => toggleCanary('stop')} testid="canary-stop">
                  <Pause className="w-3 h-3" /> Stop
                </Btn>
              : <Btn onClick={() => toggleCanary('start')} testid="canary-start">
                  <Play className="w-3 h-3" /> Start
                </Btn>}
            <Btn variant="ghost" onClick={runSweep} testid="canary-sweep" disabled={!!busy}>Sweep</Btn>
            <Btn variant="ghost" onClick={() => toggleCanary('reset')} testid="canary-reset">Reset</Btn>
          </div>
        </div>
      </div>

      {/* Dry-run table */}
      {dry && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="dry-run-panel">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-bold text-sm">Backfill Dry-run</h3>
            <span className="text-xs text-gray-500">
              total missing: <span className={`font-bold ${dry.total_missing > 0 ? 'text-rose-700' : 'text-emerald-700'}`}
                                    data-testid="dry-run-missing">{dry.total_missing}</span>
            </span>
          </div>
          <div className="max-h-60 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-3">Collection</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-right py-2 px-3">Missing</th>
                </tr>
              </thead>
              <tbody>
                {(dry.rows || []).map((r) => (
                  <tr key={r.collection} className="border-b last:border-0">
                    <td className="py-1.5 px-3 font-mono">{r.collection}</td>
                    <td className="py-1.5 px-3 text-right">{r.total ?? '—'}</td>
                    <td className={`py-1.5 px-3 text-right ${r.missing > 0 ? 'text-rose-700 font-semibold' : 'text-emerald-700'}`}>
                      {r.missing ?? r.error ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sweep results */}
      {sweep && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="sweep-panel">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-bold text-sm">Canary Sweep</h3>
            <span className="text-xs">
              <span className="text-emerald-700 font-semibold">{sweep.matches}</span> match ·
              <span className={`ml-2 font-semibold ${sweep.mismatches ? 'text-rose-700' : 'text-emerald-700'}`}
                    data-testid="sweep-mismatches">{sweep.mismatches}</span> mismatch
            </span>
          </div>
          <div className="max-h-60 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-3">Collection</th>
                  <th className="text-right py-2 px-3">Primary</th>
                  <th className="text-right py-2 px-3">Shadow</th>
                  <th className="text-right py-2 px-3">Match</th>
                </tr>
              </thead>
              <tbody>
                {(sweep.rows || []).map((r) => (
                  <tr key={r.collection} className="border-b last:border-0">
                    <td className="py-1.5 px-3 font-mono">{r.collection}</td>
                    <td className="py-1.5 px-3 text-right">{r.primary_total}</td>
                    <td className="py-1.5 px-3 text-right">{r.shadow_total}</td>
                    <td className="py-1.5 px-3 text-right">
                      {r.match
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline" />
                        : <XCircle className="w-3.5 h-3.5 text-rose-600 inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* P14-C0 — Router Status panel */}
      {flags && (
        <div className={`bg-white border rounded-xl overflow-hidden ${
          flags.kill_switch_active ? 'border-rose-300 ring-2 ring-rose-200' : ''
        }`} data-testid="router-flags-panel">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-indigo-700" />
              <h3 className="font-bold text-sm">P14-C Progress · Router Strict-Mode</h3>
              <span className="ml-2 text-xs text-gray-500">
                <span data-testid="router-progress" className="font-semibold text-indigo-700">
                  {flags.migrated} / {flags.total}
                </span>
                {' '}routers migrated ({flags.progress_pct}%)
              </span>
            </div>
            <div className="flex items-center gap-2">
              {flags.kill_switch_active && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200"
                      data-testid="kill-switch-active-badge">
                  ⚠ Kill Switch ON
                </span>
              )}
              <Btn
                variant={flags.kill_switch_active ? 'primary' : 'danger'}
                onClick={flipKillSwitch}
                testid="kill-switch-button"
                disabled={!!busy}
              >
                <Power className="w-3.5 h-3.5" />
                {flags.kill_switch_active ? 'Restore' : 'KILL SWITCH'}
              </Btn>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-gray-100">
            <div
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${flags.progress_pct}%` }}
              data-testid="router-progress-bar"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-4">Stage</th>
                  <th className="text-left py-2 px-4">Router</th>
                  <th className="text-left py-2 px-4">Category</th>
                  <th className="text-left py-2 px-4">Status</th>
                  <th className="text-right py-2 px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {(flags.rows || []).map((r) => {
                  const isStrict = r.status === 'STRICT';
                  return (
                    <tr key={r.id} data-testid={`router-row-${r.id}`}
                        className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 px-4 font-mono text-gray-500">{r.stage}</td>
                      <td className="py-2 px-4 font-mono font-semibold">{r.id}</td>
                      <td className="py-2 px-4 text-gray-600">{r.category}</td>
                      <td className="py-2 px-4">
                        <span
                          data-testid={`router-status-${r.id}`}
                          className={`px-2 py-0.5 rounded border text-[10px] uppercase ${
                            isStrict
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                              : 'bg-gray-100 text-gray-700 border-gray-200'
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-right">
                        <Btn
                          variant={isStrict ? 'ghost' : 'primary'}
                          onClick={() => toggleRouter(r.id, r.status)}
                          testid={`toggle-${r.id}`}
                          disabled={!!busy || flags.kill_switch_active}
                        >
                          {isStrict
                            ? <><ToggleRight className="w-3.5 h-3.5" /> Revert</>
                            : <><ToggleLeft className="w-3.5 h-3.5" /> Promote</>}
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t bg-gray-50 text-[10px] text-gray-500">
            Last change: {flags.updated_at || '—'} · by {flags.updated_by || 'system'}
            {' · '}Env override: <code className="font-mono">MULTI_COMPANY_STRICT_ROUTERS</code>
            {' · Global: '}<code className="font-mono">MULTI_COMPANY_ENABLED</code>
          </div>
        </div>
      )}

      {/* P14-C0.5 — Tenant Audit panel */}
      {tenantAudit && (
        <div className={`bg-white border rounded-xl overflow-hidden ${
          tenantAudit.leaks_found > 0 ? 'border-rose-300 ring-2 ring-rose-200' : ''
        }`} data-testid="tenant-audit-panel">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-indigo-700" />
              <h3 className="font-bold text-sm">Tenant Audit · Defense-in-depth</h3>
              <span className="text-xs text-gray-500">
                mode <span className={`font-mono px-1.5 py-0.5 rounded text-[10px] ${
                  tenantAudit.mode === 'block' ? 'bg-rose-100 text-rose-800' :
                  tenantAudit.mode === 'log' ? 'bg-emerald-100 text-emerald-800' :
                  'bg-gray-100 text-gray-700'
                }`} data-testid="tenant-audit-mode">{tenantAudit.mode}</span>
              </span>
              <span className="text-xs text-gray-500">
                checked <span className="font-bold text-indigo-700" data-testid="tenant-audit-checked">
                  {tenantAudit.requests_checked}
                </span>
                {' · '}leaks <span className={`font-bold ${tenantAudit.leaks_found > 0 ? 'text-rose-700' : 'text-emerald-700'}`}
                                      data-testid="tenant-audit-leaks">
                  {tenantAudit.leaks_found}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {['off', 'log', 'block'].map((m) => (
                <Btn key={m}
                     variant={tenantAudit.mode === m ? 'primary' : 'ghost'}
                     onClick={() => setAuditMode(m)}
                     testid={`audit-mode-${m}`}
                     disabled={!!busy || tenantAudit.mode === m}>
                  {m.toUpperCase()}
                </Btn>
              ))}
              <Btn variant="ghost" onClick={resetAudit} testid="audit-reset">Reset stats</Btn>
            </div>
          </div>

          {/* Per-router stats */}
          {tenantAudit.by_router && Object.keys(tenantAudit.by_router).length > 0 && (
            <div className="px-4 py-2 border-b text-xs text-gray-700">
              {Object.entries(tenantAudit.by_router).map(([rid, v]) => (
                <span key={rid} className="inline-block mr-3">
                  <code className="font-mono">{rid}</code>: {v.checked} checked
                  {v.leaks > 0 && <span className="text-rose-700 font-semibold"> · {v.leaks} leaks</span>}
                </span>
              ))}
            </div>
          )}

          {/* Leak rows */}
          {leaks.length === 0 ? (
            <div className="p-6 text-center text-xs text-emerald-700 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> No leaks recorded.
            </div>
          ) : (
            <div className="max-h-72 overflow-auto divide-y">
              {leaks.slice(0, 20).map((l, i) => (
                <div key={i} className="px-4 py-2 text-xs grid grid-cols-12 gap-2" data-testid={`leak-row-${i}`}>
                  <span className="col-span-2 text-gray-500 font-mono">{(l.ts || '').slice(0, 19)}</span>
                  <span className="col-span-2 font-mono font-semibold text-rose-700">{l.router}</span>
                  <span className="col-span-3 font-mono truncate text-gray-700">{l.path}</span>
                  <span className="col-span-2 text-gray-600">expected <code className="font-mono">{l.expected_company}</code></span>
                  <span className="col-span-3 text-rose-700 font-mono truncate">
                    found {(l.samples || []).map((s) => s.found).join(', ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Audit log */}
      <div className="bg-white border rounded-xl overflow-hidden" data-testid="audit-panel">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-bold text-sm">Migration Audit (latest 20)</h3>
        </div>
        {audit.length === 0 ? (
          <div className="p-6 text-center text-xs text-gray-500">No audit entries yet.</div>
        ) : (
          <div className="max-h-72 overflow-auto divide-y">
            {audit.map((a, i) => (
              <div key={i} className="px-4 py-2 text-xs grid grid-cols-12 gap-2">
                <span className="col-span-3 text-gray-500 font-mono">{(a.ts || '').slice(0, 19)}</span>
                <span className="col-span-3 font-semibold">{a.event || a.phase || '—'}</span>
                <span className="col-span-3 font-mono">{a.collection || a.job || a.company_id || ''}</span>
                <span className="col-span-3 text-gray-700 truncate">
                  {a.updated != null ? `updated=${a.updated}` : ''}
                  {a.primary != null && a.shadow != null
                    ? ` p=${a.primary?.total} s=${a.shadow?.total}` : ''}
                  {a.error ? `err=${a.error}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MigrationCenter;
